import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "../ui/input"
import { useState, useEffect } from "react"
import { cn } from "@/lib/utils"

interface ConfirmModalProps {
  contentClassName?: string
  isOpen: boolean
  onClose: () => void
  onConfirm: (data?: { title: string; message: string; link: string }) => void
  titleKey: string
  titleParams?: Record<string, any>
  descriptionKey?: string
  descriptionParams?: Record<string, any>
  description?: string
  confirmLabelKey?: string
  cancelLabelKey?: string
  destructive?: boolean
  prompt?: boolean
}

export default function ConfirmModal({
  contentClassName,
  isOpen,
  onClose,
  onConfirm,
  titleKey,
  titleParams,
  descriptionKey,
  descriptionParams,
  description,
  confirmLabelKey,
  cancelLabelKey,
  destructive,
  prompt,
}: ConfirmModalProps) {
  const { t } = useTranslation()
  const [messageTitle, setMessageTitle] = useState("")
  const [message, setMessage] = useState("")
  const [messageLink, setMessageLink] = useState("")

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen && prompt) {
      setMessageTitle("")
      setMessage("")
      setMessageLink("")
    }
  }, [isOpen, prompt])

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn("border sm:max-w-lg", contentClassName)}>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {String(t(titleKey, titleParams as any))}
          </DialogTitle>
          {(descriptionKey || description) && (
            <DialogDescription>
              {descriptionKey
                ? String(t(descriptionKey, descriptionParams as any))
                : (description ?? "")}
            </DialogDescription>
          )}
          {/* input for message Title general message and link to the notification */}
          {prompt && (
            <>
              <Input
                placeholder={t("notify_user.message_title", "Message Title")}
                value={messageTitle}
                onChange={(e) => setMessageTitle(e.target.value)}
              />
              <Input
                placeholder={t("notify_user.message", "Message")}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <Input
                placeholder={t("notify_user.message_link", "Message Link")}
                value={messageLink}
                onChange={(e) => setMessageLink(e.target.value)}
              />
            </>
          )}
        </DialogHeader>
        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={onClose}>
            {t(cancelLabelKey ?? "common.cancel", "Cancel")}
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            onClick={() => {
              if (prompt) {
                onConfirm({
                  title: messageTitle,
                  message: message,
                  link: messageLink,
                })
              } else {
                onConfirm()
              }
              onClose()
            }}
            disabled={prompt && (!messageTitle || !message || !messageLink)}
          >
            {t(confirmLabelKey ?? "common.ok", "OK")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
